import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import useReveal from "../lib/useReveal";
import useSeo from "../lib/useSeo";
import useJsonLd from "../lib/useJsonLd";

// Each article ties a publication → a story we covered.
// Add freely: the page will group by publication automatically.
const ARTICLES = [
  // Deepali Ghosh
  {
    publication: "India Today",
    story: "Deepali Ghosh, 70 — the rice shop that refused to close",
    title:
      "Bengal woman, 70, battles hardship alone to run crumbling eatery for 30 years",
    date: "Feb 2026",
    url: "https://www.indiatoday.in/trending-news/story/bengal-woman-70-battles-hardship-alone-to-run-crumbling-eatery-for-30-years-2866398-2026-02-11",
  },
  {
    publication: "Indian Express",
    story: "Deepali Ghosh, 70 — the rice shop that refused to close",
    title:
      "70-year-old woman, Howrah roadside rice hotel — the viral story",
    url: "https://indianexpress.com/article/trending/trending-in-india/70-year-old-woman-howrah-roadside-rice-hotel-aradhana-chatterjee-viral-video-10528833/",
  },
  {
    publication: "Economic Times",
    story: "Deepali Ghosh, 70 — the rice shop that refused to close",
    title:
      "This 70-year-old is doing everything to save her late husband's tiny rice shop — viral story leaves netizens impressed",
    url: "https://economictimes.indiatimes.com/news/new-updates/this-70-year-old-is-doing-everything-to-save-her-late-husbands-tiny-rice-shop-viral-story-leaves-netizens-impressed/articleshow/128239028.cms",
  },
  {
    publication: "NDTV",
    story: "Deepali Ghosh, 70 — the rice shop that refused to close",
    title:
      "Viral video of elderly woman fighting to keep her rice shop alive moves internet",
    url: "https://www.ndtv.com/food/watch-viral-video-of-elderly-woman-fighting-to-keep-her-rice-shop-alive-moves-internet-10987238",
  },
  {
    publication: "News18",
    story: "Deepali Ghosh, 70 — the rice shop that refused to close",
    title:
      "In a small market corner, a Bengal woman's strength is inspiring thousands",
    url: "https://www.news18.com/viral/in-a-small-market-corner-bengal-womans-strength-is-inspiring-thousands-aa-9898991.html",
  },

  // Jinali Mody / Banofi
  {
    publication: "UNEP",
    story: "Jinali Mody — banana waste into vegan leather",
    title: "Crafting leather from leaves — Young Champions of the Earth",
    url: "https://www.unep.org/youngchampions/news/video/jinali-mody-crafting-leather-leaves",
  },
  {
    publication: "The Statesman",
    story: "Jinali Mody — banana waste into vegan leather",
    title:
      "Jinali Mody wins UNEP Young Champions of the Earth prize",
    url: "https://www.thestatesman.com/india/jinali-mody-wins-unep-young-champions-of-the-earth-prize-1503490796.html",
  },
  {
    publication: "Inshorts",
    story: "Jinali Mody — banana waste into vegan leather",
    title:
      "Banofi turns banana waste into sustainable vegan leather",
    url: "https://inshorts.com/en/news/banofi-turns-banana-waste-into-sustainable-vegan-leather-1742797979132",
  },

  // Tea Stall
  {
    publication: "Al Jazeera",
    story: "A 100-year-old tea stall that runs on trust",
    title:
      "'I always come here' — the Indian tea shop that runs on trust",
    date: "Apr 2025",
    url: "https://www.aljazeera.com/features/2025/4/19/i-always-come-here-the-indian-tea-shop-that-runs-on-trust",
  },
  {
    publication: "Indian Express",
    story: "A 100-year-old tea stall that runs on trust",
    title:
      "Tea with a dash of trust — inside a Bengal shop where customers serve themselves",
    url: "https://indianexpress.com/article/cities/kolkata/tea-with-a-dash-of-trust-inside-a-bengal-shop-where-customers-serve-themselves-10035840/",
  },
  {
    publication: "Curly Tales",
    story: "A 100-year-old tea stall that runs on trust",
    title:
      "Taking self-service to another level at this 100-year-old tea stall in West Bengal",
    url: "https://curlytales.com/taking-self-service-to-another-level-at-this-100-year-old-tea-stall-in-west-bengal-customers-make-serve-their-own-tea/",
  },
];

// The marquee publications we're proud of (even without a specific linked article yet).
// Any of these without entries in ARTICLES show up as a logo-wall strip.
// URL is optional — outlets without a URL render as plain text.
const OTHER_PUBLICATIONS = [
  {
    name: "Hindustan Times",
    url: "https://www.hindustantimes.com/trending/kolkata-woman-shares-touching-video-of-elderly-musician-who-plays-on-streets-to-feed-stray-dogs-101763109016782.html",
  },
  {
    name: "The Times of India",
    url: "https://timesofindia.indiatimes.com/entertainment/events/people-want-to-help-but-need-someone-to-trust-aradhana-chatterjee-and-joydeep-sen/articleshow/132210719.cms",
  },
  { name: "Mint" },
  {
    name: "Navbharat Times",
    url: "https://navbharattimes.indiatimes.com/viral/trending/70-year-old-bengal-woman-deepali-ghosh-running-eatery-for-30-years-for-her-survival-in-howrah-west-bengal/articleshow/128191088.cms",
  },
];

function Reveal({ children, className = "" }) {
  const ref = useReveal();
  return (
    <div ref={ref} className={`sba-reveal ${className}`}>
      {children}
    </div>
  );
}

function groupByPublication(articles) {
  const map = new Map();
  articles.forEach((a) => {
    if (!map.has(a.publication)) map.set(a.publication, []);
    map.get(a.publication).push(a);
  });
  // Preserve first-seen order
  return Array.from(map.entries());
}

export default function Press() {
  useSeo({
    title: "Press & Features — Stories by Aradhana",
    description:
      "Stories by Aradhana has been featured in India Today, NDTV, Al Jazeera, Economic Times, Indian Express, UNEP, The Statesman and more. A running index of our press coverage.",
  });
  useJsonLd(
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Press & Features — Stories by Aradhana",
      url: "https://storiesbyaradhana.com/press",
      isPartOf: {
        "@type": "WebSite",
        name: "Stories by Aradhana",
        url: "https://storiesbyaradhana.com",
      },
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: ARTICLES.length,
        itemListElement: ARTICLES.map((a, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: a.url,
          name: a.title,
        })),
      },
    },
    "press-collection"
  );
  const grouped = groupByPublication(ARTICLES);
  const totalArticles = ARTICLES.length;
  const totalPublications = grouped.length + OTHER_PUBLICATIONS.length;

  return (
    <main data-testid="press-page" className="bg-[#fdfbf7]">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-16 md:pt-24 pb-16 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24 items-end">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#1a1a1a]">
                Chapter 05 — Press
              </p>
            </Reveal>
            <Reveal className="mt-8">
              <h1 className="font-serif text-[clamp(2.6rem,7vw,5.5rem)] leading-[0.98] tracking-tight text-[#1a1a1a]">
                As seen in.
              </h1>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-sans text-[17px] md:text-[18px] leading-[1.75] text-[#4a4a4a] max-w-md">
                Our stories have been picked up by India and the world&rsquo;s
                leading publications and shared across the globe — each
                article a chapter in the larger book of change we&rsquo;re
                writing.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Stat strip */}
        <div className="mt-16 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-10 border-t border-black/10 pt-10">
          {[
            { value: String(totalArticles), label: "Articles tracked" },
            { value: String(totalPublications), label: "Publications" },
            { value: "4", label: "Countries covered" },
            { value: "60M+", label: "Combined reach" },
          ].map((s, i) => (
            <Reveal key={s.label}>
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#4a4a4a]">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <p className="mt-4 font-serif text-4xl md:text-5xl tracking-tight leading-none text-[#1a1a1a]">
                  {s.value}
                </p>
                <p className="mt-3 font-sans text-sm text-[#4a4a4a]">
                  {s.label}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* GROUPED ARTICLES */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pb-20 md:pb-32">
        {grouped.map(([publication, items]) => (
          <Reveal key={publication}>
            <div
              data-testid={`press-publication-${publication.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 py-10 md:py-14 border-t border-black/10"
            >
              <div className="lg:col-span-4">
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#4a4a4a]">
                  Publication
                </p>
                <h3 className="mt-3 font-serif italic text-3xl md:text-4xl leading-tight tracking-tight text-[#1a1a1a]">
                  {publication}
                </h3>
                <p className="mt-3 font-mono text-[10px] tracking-[0.22em] uppercase text-[#2d4a22]">
                  {items.length} article{items.length > 1 ? "s" : ""}
                </p>
              </div>
              <div className="lg:col-span-8 space-y-6">
                {items.map((a) => (
                  <a
                    key={a.url}
                    href={a.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`press-article-${a.url
                      .replace(/[^a-z0-9]+/gi, "-")
                      .slice(0, 60)}`}
                    className="group block border-b border-black/5 pb-6 hover:border-[#1a1a1a]/30 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-6">
                      <div>
                        <h4 className="font-serif text-xl md:text-2xl leading-snug tracking-tight text-[#1a1a1a] group-hover:underline underline-offset-4 decoration-[#1a1a1a]/30">
                          {a.title}
                        </h4>
                        <p className="mt-2 font-sans text-sm text-[#4a4a4a]">
                          On <span className="italic">{a.story}</span>
                          {a.date ? ` · ${a.date}` : ""}
                        </p>
                      </div>
                      <ArrowUpRight
                        size={22}
                        strokeWidth={1.2}
                        className="shrink-0 mt-1 text-[#1a1a1a] opacity-60 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                      />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </Reveal>
        ))}

        {/* Other publications */}
        {OTHER_PUBLICATIONS.length > 0 && (
          <Reveal>
            <div
              data-testid="press-other-publications"
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 py-10 md:py-14 border-t border-black/10"
            >
              <div className="lg:col-span-4">
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#4a4a4a]">
                  Also featured in
                </p>
                <h3 className="mt-3 font-serif italic text-3xl md:text-4xl leading-tight tracking-tight text-[#1a1a1a]">
                  Elsewhere.
                </h3>
              </div>
              <div className="lg:col-span-8 flex flex-wrap gap-x-8 gap-y-4">
                {OTHER_PUBLICATIONS.map((p) =>
                  p.url ? (
                    <a
                      key={p.name}
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`press-elsewhere-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className="group font-serif italic text-xl md:text-2xl text-[#1a1a1a] border-b border-black/20 pb-2 hover:text-[#2d4a22] hover:border-[#2d4a22] transition-colors inline-flex items-baseline gap-2"
                    >
                      {p.name}
                      <ArrowUpRight
                        size={16}
                        strokeWidth={1.4}
                        className="translate-y-[3px] opacity-60 group-hover:opacity-100 transition-opacity"
                      />
                    </a>
                  ) : (
                    <span
                      key={p.name}
                      className="font-serif italic text-xl md:text-2xl text-[#1a1a1a]/75 border-b border-black/5 pb-2"
                    >
                      {p.name}
                    </span>
                  )
                )}
              </div>
            </div>
          </Reveal>
        )}
      </section>

      {/* PRESS CONTACT */}
      <section className="bg-[#1a1a1a] text-[#fdfbf7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <Reveal className="lg:col-span-7">
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#fdfbf7]/60">
              For the press
            </p>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Writing about a story of ours?
            </h2>
            <p className="mt-6 font-sans text-[17px] md:text-[18px] leading-relaxed text-[#fdfbf7]/75 max-w-xl">
              We&rsquo;re happy to share quotes, hi-res stills, and on-ground
              context — write to us and we&rsquo;ll get back within 48 hours.
            </p>
          </Reveal>
          <Reveal className="lg:col-span-5">
            <a
              href="mailto:hello@storiesbyaradhana.com?subject=Press%20enquiry%20%E2%80%94%20Stories%20by%20Aradhana"
              data-testid="press-contact-email"
              className="inline-flex items-center gap-3 bg-[#f9d94b] text-[#1a1a1a] px-7 py-4 text-sm font-sans tracking-[0.18em] uppercase hover:bg-[#fde277] transition-colors"
            >
              hello@storiesbyaradhana.com
              <ArrowUpRight size={16} strokeWidth={1.4} />
            </a>
            <p className="mt-6 font-mono text-[11px] tracking-[0.2em] uppercase text-[#fdfbf7]/60">
              <Link
                to="/"
                data-testid="press-back-home"
                className="hover:text-[#fdfbf7]"
              >
                ← Back to stories
              </Link>
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
